import { useState } from 'react';
import { Dialog, DialogPanel } from '@headlessui/react';
import { Bars3Icon, XMarkIcon } from '@heroicons/react/24/outline';
import { Link, useLocation } from 'react-router-dom';
import logo from '../assets/logoo.png';

const navigation = [
  { name: 'Home', href: '/' },
  { name: 'About Us', href: '/about' },
  {
    name: 'Services',
    href: '/services',
    subMenu: [
      { name: 'OHS Consulting', href: '/services/consulting' },
      { name: 'OHS Conferences', href: '/services/conferences' },
      { name: 'OHS Supplies', href: '/services/supplies' },
      { name: 'OHS Education', href: '/services/education' },
    ],
  },
  { name: 'Conference', href: '/conference' },
  { name: 'Blog', href: '/blog' },
  {
    name: 'Media',
    href: '/media',
    subMenu: [
      { name: 'Photos', href: '/media/photos' },
      { name: 'Videos', href: '/media/videos' },
      { name: 'News Articles', href: '/media/newsarticles' },
    ],
  },
  { name: 'Contact Us', href: '/contact' },
];

export default function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(''); // Track which dropdown is open
  const location = useLocation();

  const isHomePage = location.pathname === '/';

  return (
    <header className={`${isHomePage ? 'bg-black bg-opacity-70' : 'bg-inspiration-geometry bg-white'} absolute inset-x-0 top-0 z-50`}>
      <nav aria-label="Global" className="flex items-center justify-between p-2 lg:px-8">
        <div className="flex lg:flex-1">
          <Link to="/" className="">
            <span className="sr-only">OHS WORKS</span>
            <img
              alt="OHS WORKS Logo"
              src={logo}
              className="h-[80px] xl:h-[100px] ml-5 xl:ml-10 w-auto"
            />
          </Link>
        </div>
        <div className="flex lg:hidden">
          <button
            type="button"
            onClick={() => setMobileMenuOpen(true)}
            className={`-m-2.5 inline-flex items-right justify-center mr-4 rounded-md p-2.5 ${isHomePage ? 'text-gray-200' : 'text-black'}`}
          >
            <span className="sr-only">Open menu</span>
            <Bars3Icon aria-hidden="true" className="h-6 w-6" />
          </button>
        </div>
        <div className="hidden lg:flex gap-x-4 xl:gap-x-8">
          {navigation.map((item) => (
            <div
              key={item.name}
              className="relative"
              onMouseEnter={() => setDropdownOpen(item.name)}
              onMouseLeave={() => setDropdownOpen('')} // Close submenu on mouse leave
            >
              {item.subMenu ? (
                <>
                  <button
                    className={`text-md lg:text-lg font-semibold leading-6 ${isHomePage ? 'text-white hover:text-yellow-500' : 'text-black hover:text-gray-700'}`}
                  >
                    {item.name}
                  </button>
                  {dropdownOpen === item.name && (
                    <div className="absolute mt-0 w-48 bg-white shadow-lg rounded-md z-50">
                      {item.subMenu.map((subItem) => (
                        <Link
                          key={subItem.name}
                          to={subItem.href}
                          className="block px-4 py-2 text-md text-gray-700 hover:bg-gray-100"
                        >
                          {subItem.name}
                        </Link>
                      ))}
                    </div>
                  )}
                </>
              ) : (
                <Link
                  to={item.href}
                  className={`text-lg lg:text-lg font-semibold leading-6 ${isHomePage ? 'text-white hover:text-yellow-500' : 'text-black hover:text-gray-700'}`}
                >
                  {item.name}
                </Link>
              )}
            </div>
          ))}
        </div>
        <div className="hidden lg:flex lg:flex-1 lg:justify-end">
          <a href="/register" className={`text-lg font-semibold leading-6 ${isHomePage ? 'text-white' : 'text-black'}`}>
            Register Now <span aria-hidden="true">&rarr;</span>
          </a>
        </div>
      </nav>
      <div className="h-1 bg-[#D69A31]" />

      {/* Mobile menu (Drawer) */}
      <Dialog open={mobileMenuOpen} onClose={setMobileMenuOpen} className="lg:hidden">
        <div className="fixed inset-0 z-50" />
        <DialogPanel className="fixed inset-y-0 right-0 z-50 w-full overflow-y-auto bg-white px-6 py-6 sm:max-w-sm sm:ring-1 sm:ring-gray-900/10">
          <div className="flex items-center justify-between">
            <Link to="/" className="-m-1.5 p-1.5">
              <span className="sr-only">OHS WORKS</span>
              <img alt="OHS WORKS Logo" src={logo} className="h-20 w-auto" />
            </Link>
            <button
              type="button"
              onClick={() => setMobileMenuOpen(false)}
              className="-m-2.5 rounded-md p-2.5 text-gray-700"
            >
              <span className="sr-only">Close menu</span>
              <XMarkIcon aria-hidden="true" className="h-6 w-6" />
            </button>
          </div>
          <div className="h-1 mt-4 bg-gray-200"></div>
          <div className="mt-6 flow-root">
            <div className="-my-6 divide-y divide-gray-500/10">
              <div className="space-y-2 py-6">
                {navigation.map((item) => (
                  <div key={item.name}>
                    <Link
                      to={item.href}
                      className="-mx-3 block rounded-lg px-3 py-2 text-base font-semibold leading-7 text-gray-900 hover:bg-gray-50"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      {item.name}
                    </Link>
                    {item.subMenu && (
                      <div className="pl-6">
                        {item.subMenu.map((subItem) => (
                          <Link
                            key={subItem.name}
                            to={subItem.href}
                            className="-mx-3 block rounded-lg px-3 py-2 text-base font-semibold leading-7 text-gray-900 hover:bg-gray-50"
                            onClick={() => setMobileMenuOpen(false)}
                          >
                            {subItem.name}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </DialogPanel>
      </Dialog>
    </header>
  );
}
